import React, { useEffect, useState } from "react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus, X } from "lucide-react";

const AdminUsers = () => {
  const { api, user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("Admin created");
      setCreating(false);
      setForm({ name: "", email: "", password: "", role: "admin" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this admin?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline text-[#DE9E36]">Access control</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-[#1A201C] tracking-tight">
            Admins
          </h1>
          <p className="mt-2 text-sm text-[#4E5750]">
            {items.length} users · manage the people with access to the console.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="new-admin-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors"
        >
          <UserPlus size={16} /> New admin
        </button>
      </div>

      <div className="border border-[#E5E0D8] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#4E5750]">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F3EFE7]">
              <tr className="text-left">
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Created</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E0D8]">
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-[#FDFBF7]">
                  <Td className="font-semibold">{u.name}</Td>
                  <Td>{u.email}</Td>
                  <Td>
                    <span className="px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase bg-[#1A201C] text-[#DE9E36] rounded-full">
                      {u.role}
                    </span>
                  </Td>
                  <Td className="text-[#4E5750]">{new Date(u.created_at).toLocaleDateString()}</Td>
                  <Td className="text-right">
                    {me?.id !== u.id ? (
                      <button
                        onClick={() => remove(u.id)}
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs tracking-[0.2em] uppercase text-red-700 border border-red-200 hover:bg-red-50"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    ) : (
                      <span className="text-xs text-[#4E5750] italic">Current user</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setCreating(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={create}
            className="bg-[#FDFBF7] w-full max-w-md"
            data-testid="user-editor"
          >
            <div className="bg-[#1A201C] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <div className="overline text-[#DE9E36]">New admin</div>
                <div className="text-lg font-bold">Grant access</div>
              </div>
              <button type="button" onClick={() => setCreating(false)} className="text-white/60 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Name">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                  data-testid="user-name"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                  data-testid="user-email"
                />
              </Field>
              <Field label="Password (min 8 chars)">
                <input
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                  data-testid="user-password"
                />
              </Field>
              <Field label="Role">
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                </select>
              </Field>
            </div>
            <div className="border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="px-5 py-3 text-xs tracking-[0.22em] uppercase border border-[#E5E0D8]"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="user-save"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36]"
              >
                <Plus size={14} /> Create
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`px-5 py-3 overline text-[#4E5750] ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-5 py-4 align-middle ${className}`}>{children}</td>
);
const Field = ({ label, children }) => (
  <div>
    <div className="overline text-[#4E5750] mb-1.5">{label}</div>
    {children}
  </div>
);

export default AdminUsers;
